import { Injectable } from '@angular/core';
import { ID } from '@datorama/akita';
import { HttpParams } from '@angular/common/http';
import { AppointmentWizardTimeStore } from './appointment-wizard-time.store';
import {
    AppointmentWizardTime,
    appointmentWizardTimeFactory,
} from './appointment-wizard-time.model';
import { Observable } from 'rxjs';
import { LanguageService } from '../../../core/services/language/language.service';
import { SettingsQuery } from '../settings/settings.query';
import { ClinicDetailQuery } from '../clinic-detail/clinic-detail.query';
import { UserQuery } from '../user/user.query';
import { ServiceWizardQuery } from '../service-wizard/service-wizard.query';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from 'src/app/core/services/api/api.service';
import { ProfessionalWizard } from '../../models/professional-wizard.model';
import { ServicesGroupQuery } from '../services-group/services-group.query';
import { AppointmentWizardTimeQuery } from './appointment-wizard-time.query';

@Injectable({ providedIn: 'root' })
export class AppointmentWizardTimeService {
    constructor(
        private appointmentWizardTimeStore: AppointmentWizardTimeStore,
        private appointmentWizardTimeQuery: AppointmentWizardTimeQuery,
        private languageService: LanguageService,
        private settingsQuery: SettingsQuery,
        private clinicDetailQuery: ClinicDetailQuery,
        private userQuery: UserQuery,
        private serviceWizardQuery: ServiceWizardQuery,
        private apiService: ApiService,
        private servicesGroupQuery: ServicesGroupQuery
    ) {}

    /*---------------------*/
    /** OPERACIONES ENTITY */
    /*---------------------*/

    add(appointmentWizardTime: AppointmentWizardTime) {
        this.appointmentWizardTimeStore.add(appointmentWizardTime);
    }

    update(id, appointmentWizardTime: Partial<AppointmentWizardTime>) {
        this.appointmentWizardTimeStore.update(id, appointmentWizardTime);
    }

    remove(id: ID) {
        this.appointmentWizardTimeStore.remove(id);
    }

    setAppointmentWizardTimes(times: AppointmentWizardTime[]) {
        this.appointmentWizardTimeStore.set(times);
    }

    /*---------------*/
    /** LLAMADAS API */
    /*---------------*/

    /**
     * Llamada api para obtener los días disponibles de un mes para una clínica
     * GRUPOS DE SERVICIOS SINGLE
     * TODO: Ver por qué se mandan parámetros vacíos siempre
     */
    private getTimesSingleGroupApi(
        systemKey: string,
        clientToken: string,
        clinicId: string,
        services: string,
        professional: ProfessionalWizard,
        date: string
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append('SystemKey', systemKey);
        params = params.append('ClientToken', clientToken);
        params = params.append('ClinicID', clinicId);
        params = params.append('Services', services);
        params = params.append(
            'ProfessionalID',
            professional ? professional.id : ''
        );
        params = params.append(
            'ProfessionalName',
            professional ? professional.description : ''
        );
        params = params.append('DiaryDate', date);
        params = params.append('TimeEnd', '');
        params = params.append('CabineID', '');
        params = params.append('TimeStart', '');
        params = params.append('Cmd', 'c1042');
        params = params.append('Locale', this.languageService.getLocale());

        return this.apiService.callApi(params);
    }

    /**
     * Llamada api para obtener los días disponibles de un mes para una clínica
     * GRUPOS DE SERVICIOS MÚLTIPLES
     * TODO: Ver por qué se mandan parámetros vacíos siempre
     */
    private getTimesMultipleGroupApi(
        systemKey: string,
        clinicId: string,
        date: string,
        servicesGroups: any
    ): Observable<any> {
        let params = new HttpParams();
        params = params.append('SystemKey', systemKey);
        params = params.append('ClinicID', clinicId);
        params = params.append('ServicesGroups', servicesGroups);
        params = params.append('DiaryDate', date);
        params = params.append('Cmd', 'c1046');
        params = params.append('Locale', this.languageService.getLocale());

        return this.apiService.callApi(params);
    }

    /*------------*/
    /** SERVICIOS */
    /*------------*/

    /**
     * Llama a la API para obtener las horas posibles para reservar la cita
     * TODO: Validar el [0] que le he puesto al profesional (¿Y si no hay ninguno peta?) ¿Cambiar a Active?
     */
    async getTimesSingleGroup(date: NgbDateStruct, currentClinicId: string) {
        /** Los servicios seleccionados los cogemos de Akita */
        const servicesChekedArrayId = [];
        const servicesCheked = this.serviceWizardQuery
            .getAll({
                filterBy: ({ checked }) => checked === true,
            })
            .forEach((service) => {
                servicesChekedArrayId.push(service.id);
            });

        const professionalChecked: ProfessionalWizard = this.servicesGroupQuery.getAll()[0]
            .professionalSelected;
        try {
            const dates = await this.getTimesSingleGroupApi(
                this.settingsQuery.systemKeyURL,
                this.userQuery.tokenId,
                currentClinicId,
                servicesChekedArrayId.join(','),
                professionalChecked,
                `${date.year}-${date.month}-${date.day}`
            ).toPromise();

            /**
             * Cargamos los huecos a la Store
             */
            this.setAppointmentWizardTimesByApi(dates.DiaryOptions);
        } catch (error) {
            this.setAppointmentWizardTimesByApi(null);
        }
    }

    /** Cargar las horas disponibles cuando tenemos múltiples grupos de citas */
    async getTimesMultipleGroup(date: NgbDateStruct) {
        const servicesGroups = [];
        this.servicesGroupQuery.getAll().forEach((serviceGroup) => {
            servicesGroups.push({
                ServicesID: serviceGroup.ids,
                ServicesProfessionalID:
                    serviceGroup.professionalSelected?.id ?? '0',
                ServicesClinicID: serviceGroup.clinicId, //TODO CONFIRMAR c1046 de petición de jorge ¿Hace falta también con los simples?
            });
        });
        const servicesGroupJson = {
            ServicesGroups: servicesGroups,
        };
        try {
            const dates = await this.getTimesMultipleGroupApi(
                this.settingsQuery.systemKeyURL,
                this.clinicDetailQuery.id,
                `${date.year}-${date.month}-${date.day}`,
                JSON.stringify(servicesGroupJson)
            ).toPromise();

            /**
             * Cargamos los huecos a la Store
             */
            this.setAppointmentWizardTimesByApi(dates.DiaryOptions);
        } catch (error) {
            this.setAppointmentWizardTimesByApi(null);
        }
    }

    /**
     * Crea las citas a partir de una Array y las carga en el Store
     */
    setAppointmentWizardTimesByApi(times) {
        const appointmentWizardTimes = [];
        if (times) {
            times.forEach((time) => {
                appointmentWizardTimes.push(appointmentWizardTimeFactory(time));
            });
        }

        this.setAppointmentWizardTimes(appointmentWizardTimes);
    }

    setActive(appointmentWizardTimeId: string) {
        this.appointmentWizardTimeStore.setActive(appointmentWizardTimeId);
    }

    selectActive(): Observable<AppointmentWizardTime> {
        return this.appointmentWizardTimeQuery.selectActive();
    }

    resetStore() {
        this.appointmentWizardTimeStore.reset();
    }
}
